import React, { useMemo, memo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  Tooltip,
} from "recharts";

import { formatNumber } from "./utils";
import useThemeColors from "hooks/useThemeColors";

const useStyles = makeStyles({
  chartContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  chartArea: {
    flex: "1 1 auto",
    minHeight: "150px",
  },
  // Legend styles
  legendContainer: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    marginTop: "4px",
    marginBottom: "4px",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "10px",
    color: "#666",
  },
  legendLine: {
    width: "20px",
    height: "2px",
  },
  legendLineDashed: {
    width: "20px",
    height: "2px",
    backgroundImage:
      "linear-gradient(to right, currentColor 50%, transparent 50%)",
    backgroundSize: "6px 2px",
  },
  // Summary table styles
  tableContainer: {
    flex: "0 0 auto",
    marginTop: "8px",
  },
  summaryTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "12px",
  },
  tableHeader: {
    backgroundColor: "#f5f5f5",
    "& th": {
      padding: "4px 5px",
      textAlign: "center",
      fontWeight: 600,
      color: "#555",
      borderBottom: "1px solid #ddd",
      whiteSpace: "nowrap",
    },
    "& th:first-child": {
      textAlign: "left",
    },
  },
  tableBody: {
    "& td": {
      padding: "4px 5px",
      textAlign: "center",
      color: "#666",
      borderBottom: "1px solid #eee",
    },
    "& td:first-child": {
      textAlign: "left",
      fontWeight: 500,
    },
  },
  groupCell: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  groupBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "18px",
    height: "18px",
    borderRadius: "3px",
    color: "#fff",
    fontWeight: 600,
    fontSize: "12px",
    flexShrink: 0,
  },
  groupDefinition: {
    color: "#666",
    fontSize: "11px",
    whiteSpace: "nowrap",
  },
  positiveValue: {
    color: "#2e7d32",
  },
  negativeValue: {
    color: "#c62828",
  },
  dividerCell: {
    borderLeft: "2px solid #ccc !important",
  },
  totalRow: {
    backgroundColor: "#f0f0f0",
    fontWeight: 600,
    "& td": {
      borderTop: "2px solid #999",
    },
  },
  // Tooltip styles
  tooltipContainer: {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    border: "1px solid #ddd",
    borderRadius: "4px",
    padding: "8px 12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
    fontSize: "11px",
  },
  tooltipTitle: {
    fontWeight: 600,
    color: "#333",
    marginBottom: "6px",
    borderBottom: "1px solid #eee",
    paddingBottom: "4px",
  },
  tooltipRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    marginTop: "3px",
    color: "#666",
  },
  tooltipLabel: {
    color: "#888",
  },
  tooltipValue: {
    fontWeight: 500,
    color: "#333",
  },
  tooltipGroup: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    borderRadius: "3px",
    color: "#fff",
    fontWeight: 600,
    fontSize: "10px",
  },
});

// Get color based on group (A = client color, B/C/D = grays)
const getGroupColorFn = (group, clientColor) => {
  switch (group) {
    case "A":
      return clientColor;
    case "B":
      return "#555"; // Dark gray
    case "C":
      return "#999"; // Medium gray
    case "D":
      return "#ccc"; // Light gray
    default:
      return "#999";
  }
};

/**
 * Custom tooltip component for the Pareto chart
 */
function CustomTooltip({ active, payload, classes, clientColor }) {
  const { clientTextColor } = useThemeColors();
  if (!active || !payload || !payload.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  // Get groups present in this bucket
  const groupsPresent = data.groupsPresent || [data.primaryGroup || data.group];
  const hasMultipleGroups = groupsPresent.length > 1;

  return (
    <div className={classes.tooltipContainer}>
      <div className={classes.tooltipTitle}>
        {data.bucketLabel?.replace("%", "") || "-"}% dos clientes
      </div>
      <div className={classes.tooltipRow}>
        <span className={classes.tooltipLabel}>Faturamento:</span>
        <span className={classes.tooltipValue}>
          {data.share?.toFixed(1) || "-"}%
        </span>
      </div>
      <div className={classes.tooltipRow}>
        <span className={classes.tooltipLabel}>
          {hasMultipleGroups ? "Grupos:" : "Grupo:"}
        </span>
        <span style={{ display: "flex", gap: "3px" }}>
          {groupsPresent.map((g) => (
            <span
              key={g}
              className={classes.tooltipGroup}
              style={{
                backgroundColor: getGroupColorFn(g, clientColor),
                ...(g === "A" ? { color: clientTextColor } : {}),
              }}
            >
              {g}
            </span>
          ))}
        </span>
      </div>
      <div className={classes.tooltipRow}>
        <span className={classes.tooltipLabel}>Margem de Servir:</span>
        <span className={classes.tooltipValue}>
          {data.avgServingMargin?.toFixed(1) || "-"}%
        </span>
      </div>
    </div>
  );
}

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  classes: PropTypes.object.isRequired,
  clientColor: PropTypes.string.isRequired,
};

/**
 * ParetoChart component
 * Displays a Pareto/ABCD concentration chart for clients
 * - Group A: clients representing 0-20% of sales (client color)
 * - Group B: clients representing 20-50% of sales (dark gray)
 * - Group C: clients representing 50-80% of sales (medium gray)
 * - Group D: clients representing 80-100% of sales (light gray)
 */
function ParetoChart({ data, preAggregatedData, clientColor = "#3f51b5" }) {
  const classes = useStyles();
  const { clientTextColor } = useThemeColors();
  const { t } = useTranslation();

  // Use pre-aggregated data if available, otherwise calculate from raw data
  const chartData = useMemo(() => {
    // If pre-aggregated data is provided, use it directly
    if (preAggregatedData?.buckets && preAggregatedData.buckets.length > 0) {
      return preAggregatedData.buckets;
    }

    // Otherwise, calculate from raw data
    if (!data || data.length === 0) return [];

    // Sort by share descending
    const sorted = [...data].sort((a, b) => b.share - a.share);

    // Calculate cumulative percentages
    const totalShare = sorted.reduce((sum, item) => sum + item.share, 0);
    let cumulative = 0;
    let productCount = 0;

    const paretoData = sorted.map((item) => {
      cumulative += item.share;
      productCount += 1;
      const cumulativePercent = (cumulative / totalShare) * 100;
      const productPercent = (productCount / sorted.length) * 100;

      // ABCD: element that crosses a threshold belongs to the lower group
      const prevCumulative =
        cumulativePercent - (item.share / totalShare) * 100;
      let group;
      if (prevCumulative < 20) {
        group = "A";
      } else if (prevCumulative < 50) {
        group = "B";
      } else if (prevCumulative < 80) {
        group = "C";
      } else {
        group = "D";
      }

      return {
        name: item.name,
        share: item.share,
        cumulative: cumulativePercent,
        productPercent,
        group,
      };
    });

    // Aggregate into percentage buckets
    const buckets = [];
    const bucketSize = 2; // 2% buckets for finer granularity
    const numBuckets = 100 / bucketSize;

    for (let i = 0; i < numBuckets; i++) {
      const startPercent = i * bucketSize;
      const endPercent = (i + 1) * bucketSize;

      // Find items in this bucket
      const bucketItems = paretoData.filter(
        (item) =>
          item.productPercent > startPercent &&
          item.productPercent <= endPercent,
      );

      // Sum the shares for this bucket
      const bucketShare = bucketItems.reduce(
        (sum, item) => sum + item.share,
        0,
      );

      // Get cumulative at end of bucket
      const cumulativeAtBucket =
        paretoData
          .filter((item) => item.productPercent <= endPercent)
          .reduce((max, item) => Math.max(max, item.cumulative), 0) || 0;

      // Determine group based on cumulative (ABCD: 20/50/80)
      let group;
      if (cumulativeAtBucket <= 20) {
        group = "A";
      } else if (cumulativeAtBucket <= 50) {
        group = "B";
      } else if (cumulativeAtBucket <= 80) {
        group = "C";
      } else {
        group = "D";
      }

      // Per-group share breakdown
      const shareByGroup = { A: 0, B: 0, C: 0, D: 0 };
      bucketItems.forEach((item) => {
        shareByGroup[item.group] = (shareByGroup[item.group] || 0) + item.share;
      });

      buckets.push({
        productPercent: endPercent,
        bucketLabel: `${startPercent}-${endPercent}%`,
        share: bucketShare,
        shareA: shareByGroup.A,
        shareB: shareByGroup.B,
        shareC: shareByGroup.C,
        shareD: shareByGroup.D,
        cumulative: cumulativeAtBucket,
        group,
        avgServingMargin: 10,
      });
    }

    return buckets;
  }, [data, preAggregatedData]);

  // Get summary data
  const summaryData = useMemo(() => {
    if (preAggregatedData?.summary) {
      return preAggregatedData.summary;
    }
    // Default summary if not provided
    return {
      groupA: {
        elements: 0,
        avgServingMargin: 0,
        deltaVolume: 0,
        deltaAvgPrice: 0,
        deltaRevenue: 0,
        deltaMargin: 0,
        deltaMarginPct: 0,
      },
      groupB: {
        elements: 0,
        avgServingMargin: 0,
        deltaVolume: 0,
        deltaAvgPrice: 0,
        deltaRevenue: 0,
        deltaMargin: 0,
        deltaMarginPct: 0,
      },
      groupC: {
        elements: 0,
        avgServingMargin: 0,
        deltaVolume: 0,
        deltaAvgPrice: 0,
        deltaRevenue: 0,
        deltaMargin: 0,
        deltaMarginPct: 0,
      },
      groupD: {
        elements: 0,
        avgServingMargin: 0,
        deltaVolume: 0,
        deltaAvgPrice: 0,
        deltaRevenue: 0,
        deltaMargin: 0,
        deltaMarginPct: 0,
      },
    };
  }, [preAggregatedData]);

  // Get color based on group
  const getGroupColor = (group) => getGroupColorFn(group, clientColor);

  // Find where each group ends (for reference lines) - based on cumulative 20/50/80
  // These represent the bucket INDEX where the group changes
  const groupAEndIndex = chartData.findIndex((d) => d.cumulative > 20);
  const groupBEndIndex = chartData.findIndex((d) => d.cumulative > 50);
  const groupCEndIndex = chartData.findIndex((d) => d.cumulative > 80);

  // Format delta value with color
  const formatDelta = (value, isPercent = false, isCurrency = false) => {
    const isPositive = value >= 0;
    const className = isPositive
      ? classes.positiveValue
      : classes.negativeValue;
    let formatted;
    if (isCurrency) {
      formatted = formatNumber(value, "currency", 0);
    } else if (isPercent) {
      formatted = `${isPositive ? "+" : ""}${value.toFixed(1)}%`;
    } else {
      formatted = `${isPositive ? "+" : ""}${value.toFixed(1)}%`;
    }
    return <span className={className}>{formatted}</span>;
  };

  // Format revenue value with magnitude (K, M, B) - no currency symbol
  const formatRevenue = (value) => {
    if (value === null || value === undefined) return "-";
    const absValue = Math.abs(value);
    if (absValue >= 1000000000) {
      return `${(value / 1000000000).toFixed(1)}B`;
    } else if (absValue >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (absValue >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return `${value.toFixed(1)}`;
  };

  return (
    <div className={classes.chartContainer}>
      {/* Chart Area */}
      <div className={classes.chartArea}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 18, right: 5, left: -10, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#eee"
              vertical={false}
            />
            <XAxis
              dataKey="bucketLabel"
              tick={{ fontSize: 10, fill: "#666" }}
              tickLine={false}
              axisLine={{ stroke: "#ddd" }}
              interval={0}
            />
            {/* Left Y-Axis (0-100%) for bars and cumulative line */}
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 12, fill: "#666" }}
              tickLine={false}
              axisLine={false}
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
            />
            {/* Right Y-Axis (hidden - serving margin line uses this scale) */}
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={false}
              tickLine={false}
              axisLine={false}
              domain={[0, 35]}
            />

            {/* Tooltip */}
            <Tooltip
              content={
                <CustomTooltip classes={classes} clientColor={clientColor} />
              }
            />

            {/* Reference lines for ABCD boundaries (vertical) - positioned BETWEEN bars */}
            {groupAEndIndex >= 0 && (
              <ReferenceLine
                x={chartData[groupAEndIndex]?.bucketLabel}
                yAxisId="left"
                stroke="#c62828"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: "A|B",
                  position: "insideTopRight",
                  fontSize: 9,
                  fill: "#c62828",
                  offset: 5,
                }}
              />
            )}
            {groupBEndIndex >= 0 && (
              <ReferenceLine
                x={chartData[groupBEndIndex]?.bucketLabel}
                yAxisId="left"
                stroke="#c62828"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: "B|C",
                  position: "insideTopRight",
                  fontSize: 9,
                  fill: "#c62828",
                  offset: 5,
                }}
              />
            )}
            {groupCEndIndex >= 0 && (
              <ReferenceLine
                x={chartData[groupCEndIndex]?.bucketLabel}
                yAxisId="left"
                stroke="#c62828"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: "C|D",
                  position: "insideTopRight",
                  fontSize: 9,
                  fill: "#c62828",
                  offset: 5,
                }}
              />
            )}

            {/* Horizontal reference lines for cumulative percentages (20/50/80) */}
            <ReferenceLine
              y={20}
              yAxisId="left"
              stroke="#ddd"
              strokeDasharray="2 2"
              strokeWidth={1}
            />
            <ReferenceLine
              y={50}
              yAxisId="left"
              stroke="#ddd"
              strokeDasharray="2 2"
              strokeWidth={1}
            />
            <ReferenceLine
              y={80}
              yAxisId="left"
              stroke="#ddd"
              strokeDasharray="2 2"
              strokeWidth={1}
            />

            {/* Bars — stacked by ABCD group within each bucket */}
            <Bar
              yAxisId="left"
              dataKey="shareA"
              stackId="share"
              radius={[0, 0, 0, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell key={`a-${index}`} fill={getGroupColor("A")} />
              ))}
            </Bar>
            <Bar
              yAxisId="left"
              dataKey="shareB"
              stackId="share"
              radius={[0, 0, 0, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell key={`b-${index}`} fill={getGroupColor("B")} />
              ))}
            </Bar>
            <Bar
              yAxisId="left"
              dataKey="shareC"
              stackId="share"
              radius={[0, 0, 0, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell key={`c-${index}`} fill={getGroupColor("C")} />
              ))}
            </Bar>
            <Bar
              yAxisId="left"
              dataKey="shareD"
              stackId="share"
              radius={[2, 2, 0, 0]}
            >
              {chartData.map((entry, index) => (
                <Cell key={`d-${index}`} fill={getGroupColor("D")} />
              ))}
            </Bar>

            {/* Cumulative line (Pareto curve) */}
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="cumulative"
              stroke="#333"
              strokeWidth={2}
              dot={false}
              name="Acumulado"
            />

            {/* Serving margin line */}
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="avgServingMargin"
              stroke="#2e7d32"
              strokeWidth={2}
              dot={false}
              strokeDasharray="4 2"
              name="Margem de Servir"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Legend for lines */}
      <div className={classes.legendContainer}>
        <div className={classes.legendItem}>
          <div
            className={classes.legendLine}
            style={{ backgroundColor: "#333" }}
          />
          <span>% Acumulado Faturamento</span>
        </div>
        <div className={classes.legendItem}>
          <div
            className={classes.legendLineDashed}
            style={{ color: "#2e7d32" }}
          />
          <span>Margem de Servir %</span>
        </div>
      </div>

      {/* Summary Table */}
      <div className={classes.tableContainer}>
        <table className={classes.summaryTable}>
          <thead className={classes.tableHeader}>
            <tr>
              <th>Grupo</th>
              <th># clientes</th>
              <th>Fat Médio</th>
              <th>Mg Serv</th>
              <th>% Fat</th>
              <th>% Mg</th>
              <th className={classes.dividerCell}>Δ Vol</th>
              <th>Δ Preço</th>
              <th>Δ Fat</th>
              <th>Δ Mg $</th>
              <th>Δ Mg %</th>
            </tr>
          </thead>
          <tbody className={classes.tableBody}>
            {(() => {
              // Calculate totals to determine each group's share
              const groups = ["A", "B", "C", "D"];
              const groupRevenues = groups.map((g) => {
                const gd = summaryData[`group${g}`];
                if (!gd) return 0;
                return (gd.elements || 0) * (gd.avgRevenuePerClient || 0);
              });
              const groupMargins = groups.map((g, i) => {
                const gd = summaryData[`group${g}`];
                if (!gd) return 0;
                return groupRevenues[i] * ((gd.avgServingMargin || 0) / 100);
              });
              const totalRevenue = groupRevenues.reduce((a, b) => a + b, 0);
              const totalMargin = groupMargins.reduce((a, b) => a + b, 0);

              // Calculate totals for the footer row
              const totalClients = groups.reduce(
                (acc, g) => acc + (summaryData[`group${g}`]?.elements || 0),
                0,
              );
              const avgMarginPct =
                totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;

              const rows = [
                {
                  key: "A",
                  label: t("alignment_session.abc_concentration.group_a"),
                },
                {
                  key: "B",
                  label: t("alignment_session.abc_concentration.group_b"),
                },
                {
                  key: "C",
                  label: t("alignment_session.abc_concentration.group_c"),
                },
                {
                  key: "D",
                  label: t("alignment_session.abc_concentration.group_d"),
                },
              ].map(({ key, label }, idx) => {
                const groupData = summaryData[`group${key}`];
                const revenueShare =
                  totalRevenue > 0
                    ? ((groupRevenues[idx] / totalRevenue) * 100).toFixed(1)
                    : "-";
                const marginShare =
                  totalMargin > 0
                    ? ((groupMargins[idx] / totalMargin) * 100).toFixed(1)
                    : "-";
                // Subtle delta helper for static columns
                const subtleDelta = (curr, prev, fmt = "abs") => {
                  if (prev === undefined || prev === null) return null;
                  const diff = curr - prev;
                  if (Math.abs(diff) < 0.05) return null;
                  const sign = diff >= 0 ? "+" : "";
                  let text;
                  if (fmt === "abs") text = `${sign}${Math.round(diff)}`;
                  else if (fmt === "rev")
                    text = `${sign}${formatRevenue(diff)}`;
                  else if (fmt === "pp") text = `${sign}${diff.toFixed(1)}pp`;
                  else text = `${sign}${diff.toFixed(1)}`;
                  const clr =
                    diff > 0 ? "#2e7d32" : diff < 0 ? "#c62828" : "#aaa";
                  return (
                    <span
                      style={{
                        fontSize: "10px",
                        color: clr,
                        marginLeft: "3px",
                        fontWeight: 500,
                      }}
                    >
                      ({text})
                    </span>
                  );
                };

                const elPrev = groupData?.elements_prev;
                const avgRevPrev = groupData?.avgRevenuePerClient_prev;
                const avgSmPrev = groupData?.avgServingMargin_prev;
                const revSharePrev = groupData?.cumulativeShare_prev;
                const mrgSharePrev = groupData?.marginShare_prev;

                return (
                  <tr key={key}>
                    <td>
                      <div className={classes.groupCell}>
                        <span
                          className={classes.groupBadge}
                          style={{
                            backgroundColor: getGroupColor(key),
                            ...(key === "A" ? { color: clientTextColor } : {}),
                          }}
                        >
                          {key}
                        </span>
                        <span className={classes.groupDefinition}>{label}</span>
                      </div>
                    </td>
                    <td>
                      {groupData?.elements || 0}
                      {subtleDelta(groupData?.elements || 0, elPrev, "abs")}
                    </td>
                    <td>
                      {formatRevenue(groupData?.avgRevenuePerClient)}
                      {subtleDelta(
                        groupData?.avgRevenuePerClient || 0,
                        avgRevPrev,
                        "rev",
                      )}
                    </td>
                    <td>
                      {groupData?.avgServingMargin?.toFixed(1) || "-"}%
                      {subtleDelta(
                        groupData?.avgServingMargin || 0,
                        avgSmPrev,
                        "pp",
                      )}
                    </td>
                    <td>
                      {revenueShare}%
                      {subtleDelta(
                        parseFloat(revenueShare) || 0,
                        revSharePrev,
                        "pp",
                      )}
                    </td>
                    <td>
                      {marginShare}%
                      {subtleDelta(
                        parseFloat(marginShare) || 0,
                        mrgSharePrev,
                        "pp",
                      )}
                    </td>
                    <td className={classes.dividerCell}>
                      {formatDelta(groupData?.deltaVolume || 0)}
                    </td>
                    <td>{formatDelta(groupData?.deltaAvgPrice || 0)}</td>
                    <td>{formatDelta(groupData?.deltaRevenue || 0)}</td>
                    <td>
                      {formatDelta(groupData?.deltaMargin || 0, false, true)}
                    </td>
                    <td>{formatDelta(groupData?.deltaMarginPct || 0, true)}</td>
                  </tr>
                );
              });

              // Calculate total deltas (weighted by revenue share)
              const totalDeltaVolume =
                groups.reduce((acc, g) => {
                  const gd = summaryData[`group${g}`];
                  return (
                    acc + (gd?.deltaVolume || 0) * (gd?.cumulativeShare || 0)
                  );
                }, 0) / 100;
              const totalDeltaAvgPrice =
                groups.reduce((acc, g) => {
                  const gd = summaryData[`group${g}`];
                  return (
                    acc + (gd?.deltaAvgPrice || 0) * (gd?.cumulativeShare || 0)
                  );
                }, 0) / 100;
              const totalDeltaRevenue =
                groups.reduce((acc, g) => {
                  const gd = summaryData[`group${g}`];
                  return (
                    acc + (gd?.deltaRevenue || 0) * (gd?.cumulativeShare || 0)
                  );
                }, 0) / 100;
              const totalDeltaMargin = groups.reduce((acc, g) => {
                const gd = summaryData[`group${g}`];
                return acc + (gd?.deltaMargin || 0);
              }, 0);
              const totalDeltaMarginPct =
                groups.reduce((acc, g) => {
                  const gd = summaryData[`group${g}`];
                  return (
                    acc + (gd?.deltaMarginPct || 0) * (gd?.cumulativeShare || 0)
                  );
                }, 0) / 100;

              // Add total row
              rows.push(
                <tr key="total" className={classes.totalRow}>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td>{totalClients}</td>
                  <td>{formatRevenue(totalRevenue / totalClients)}</td>
                  <td>{avgMarginPct.toFixed(1)}%</td>
                  <td>100%</td>
                  <td>100%</td>
                  <td className={classes.dividerCell}>
                    {formatDelta(totalDeltaVolume)}
                  </td>
                  <td>{formatDelta(totalDeltaAvgPrice)}</td>
                  <td>{formatDelta(totalDeltaRevenue)}</td>
                  <td>{formatDelta(totalDeltaMargin, false, true)}</td>
                  <td>{formatDelta(totalDeltaMarginPct, true)}</td>
                </tr>,
              );

              return rows;
            })()}
          </tbody>
        </table>
      </div>
    </div>
  );
}

ParetoChart.propTypes = {
  // Raw data (used if preAggregatedData is not provided)
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      share: PropTypes.number,
    }),
  ),
  // Pre-aggregated data from backend (preferred for performance)
  preAggregatedData: PropTypes.shape({
    totalElements: PropTypes.number,
    summary: PropTypes.object,
    buckets: PropTypes.arrayOf(
      PropTypes.shape({
        bucketLabel: PropTypes.string,
        share: PropTypes.number,
        cumulative: PropTypes.number,
        group: PropTypes.string,
        avgServingMargin: PropTypes.number,
      }),
    ),
  }),
  clientColor: PropTypes.string,
};

export default memo(ParetoChart);
